/**
 * AddPhaseModal — three-stage "Add phase" flow from the Schedule root ⋯ menu.
 *
 *   Stage 1 — is the phase already in BQE Core?  link (paste its Core id) | create (Cortex makes it)
 *   Stage 2 — phase type:                         Basic Services | Additional Services | Long Distance Travel
 *   Stage 3 — the inputs for the chosen type (+ the Core id when linking)
 *
 * All paths go through services/phases.addPhase → api/add-phase, which links or creates the Core
 * phase and writes one Airtable Phase row (Manual Add ✓ + the type-specific fields). Basic Services
 * can auto-populate milestones — the `createMilestones` flag now triggers the IN-APP scaffolder
 * (api/_scaffoldMilestones reads the Supabase WBS template), not the retired Airtable automation.
 *
 * Opens on `open-add-phase-modal` (detail.projectId); mounted once in OutlineView. Reloads the
 * project on success.
 */

import { useEffect, useState } from 'react'
import { createPortal } from 'react-dom'
import { ctx } from '../outline/ctx.js'
import { addPhase } from '../services/phases.js'

// Standard (Basic Services) phase names, in schedule order — the Phase Name single-select minus the
// two special types. Mirrors the Phases table choices.
const BASIC_PHASES = [
  'Design Consultation Phase', 'Pre-design & Planning Phase', 'Schematic Design Phase',
  'Discretionary Approvals Phase', 'Design Development Phase', 'Construction Documents Phase',
  'Permitting Phase', 'Architectural Detailing Phase', 'Interior Architecture Phase',
  'Bidding & Negotiation Phase', 'Construction Support Phase', 'Hourly Project Meetings',
  'Existing Building 3D Model',
]

const todayISO = () => new Date().toISOString().split('T')[0]

export default function AddPhaseModal() {
  const [open, setOpen] = useState(false)
  const [step, setStep] = useState(1)
  const [projId, setProjId] = useState('')
  const [inCore, setInCore] = useState(null)        // 'link' | 'create'
  const [phaseType, setPhaseType] = useState(null)  // 'basic' | 'additional' | 'travel'
  // shared / typed fields
  const [bqeCoreId, setBqeCoreId] = useState('')
  const [basicName, setBasicName] = useState('')
  const [contractType, setContractType] = useState('Fixed Fee')
  const [fee, setFee] = useState('')
  const [createMilestones, setCreateMilestones] = useState(true)
  const [asNumber, setAsNumber] = useState('')
  const [asTitle, setAsTitle] = useState('')
  const [ldtNumber, setLdtNumber] = useState('')
  const [ldtDate, setLdtDate] = useState(todayISO())
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    function onOpen(e) {
      setProjId((e && e.detail && e.detail.projectId) || ctx.recordId || '')
      setStep(1); setInCore(null); setPhaseType(null)
      setBqeCoreId(''); setBasicName(''); setContractType('Fixed Fee'); setFee(''); setCreateMilestones(true)
      setAsNumber(''); setAsTitle(''); setLdtNumber(''); setLdtDate(todayISO())
      setError(''); setSaving(false)
      setOpen(true)
    }
    window.addEventListener('open-add-phase-modal', onOpen)
    return () => window.removeEventListener('open-add-phase-modal', onOpen)
  }, [])

  function close() { if (!saving) setOpen(false) }

  async function submit() {
    if (!projId) { setError('No project in context. Open a project, then add the phase.'); return }
    const linking = inCore === 'link'
    const ct = phaseType === 'travel' ? 'Fixed Fee' : contractType
    // Validate
    if (linking && !bqeCoreId.trim()) { setError('Enter the BQE Core ID.'); return }
    if (phaseType === 'basic' && !basicName) { setError('Choose a phase.'); return }
    if (phaseType === 'additional' && !String(asNumber).trim()) { setError('Enter the Additional Services number.'); return }
    if (phaseType === 'travel' && !String(ldtNumber).trim()) { setError('Enter the Long Distance Travel number.'); return }
    if (!linking && ct === 'Fixed Fee' && !(Number(fee) > 0)) {
      setError(phaseType === 'travel' ? 'Enter the day-rate fee.' : 'A Fixed Fee phase created in Core needs a fee greater than zero.'); return
    }

    const params = {
      projectRecordId: projId,
      phaseType,
      phaseName: phaseType === 'additional' ? 'Additional Services' : phaseType === 'travel' ? 'Long Distance Travel' : basicName,
      contractType: ct,
      fee: ct === 'Fixed Fee' ? Number(fee) || 0 : 0,
      lastUpdateByMemberId: (ctx.currentMember && ctx.currentMember.id) || undefined,
    }
    if (linking) params.bqeCoreId = bqeCoreId.trim()
    if (phaseType === 'basic') params.createMilestones = !!createMilestones
    if (phaseType === 'additional') { params.additionalServicesNumber = asNumber; params.customTitle = asTitle.trim() }
    if (phaseType === 'travel') { params.longDistanceTravelNumber = ldtNumber; params.longDistanceTravelDate = ldtDate }

    setSaving(true); setError('')
    try {
      const res = await addPhase(params)
      if (res && res.error) throw new Error(typeof res.error === 'string' ? res.error : (res.error.message || 'Add failed'))
      setOpen(false)
      if (ctx.reloadProjectData) ctx.reloadProjectData(projId)
    } catch (err) {
      setError((err && err.message) || 'Could not add the phase.')
      setSaving(false)
    }
  }

  if (!open) return null

  const stepLabel = 'Step ' + step + ' of 3'
  const stepTitle = step === 1 ? 'Is the phase already in BQE Core?' : step === 2 ? 'What kind of phase?' : 'Phase details'

  const label = { fontFamily: '"DM Sans",sans-serif', fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 5, display: 'block' }
  const input = { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '8px 10px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' }
  const mono = { ...input, fontFamily: '"DM Mono",monospace' }
  const btnBase = { border: 'none', borderRadius: 4, padding: '9px 20px', fontFamily: '"DM Sans",sans-serif', fontSize: 13, fontWeight: 500, cursor: 'pointer' }

  // A big stacked choice button used in stages 1 & 2.
  function Choice({ title, sub, onClick, active }) {
    return (
      <button onClick={onClick} style={{
        textAlign: 'left', padding: '12px 14px', borderRadius: 8, cursor: 'pointer', width: '100%',
        border: '0.5px solid ' + (active ? 'var(--aj-text)' : 'var(--aj-border-mid)'), background: active ? 'var(--aj-bg-subtle)' : 'var(--aj-bg-elevated)',
      }}>
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 13, color: 'var(--aj-text)' }}>{title}</div>
        {sub && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 }}>{sub}</div>}
      </button>
    )
  }

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div data-modal-id="AddPhaseModal" style={{ background: 'var(--aj-bg-elevated)', borderRadius: 12, border: '0.5px solid var(--aj-border)', width: 460, maxWidth: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '16px 20px', borderBottom: '0.5px solid var(--aj-border)' }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 9, letterSpacing: '0.14em', textTransform: 'uppercase', color: 'var(--aj-text-muted)', marginBottom: 2 }}>{stepLabel}</div>
            <div style={{ fontFamily: '"DM Sans",sans-serif', fontWeight: 500, fontSize: 13, color: 'var(--aj-text)' }}>{stepTitle}</div>
          </div>
          <button onClick={close} style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--aj-text-muted)', padding: 4 }}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none"><path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/></svg>
          </button>
        </div>

        {/* Body */}
        <div style={{ padding: '18px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {step === 1 && (<>
            <Choice title="Already created in BQE Core" sub="Paste its Core record id to link it." active={inCore === 'link'}
              onClick={() => { setInCore('link'); setStep(2); setError('') }} />
            <Choice title="Create it in BQE Core" sub="Cortex makes the phase in Core under this project and links it." active={inCore === 'create'}
              onClick={() => { setInCore('create'); setStep(2); setError('') }} />
          </>)}

          {step === 2 && (<>
            <Choice title="Basic Services" sub="A standard phase (Pre-design, SD, DD, CD, …). Can auto-populate milestones." active={phaseType === 'basic'}
              onClick={() => { setPhaseType('basic'); setStep(3); setError('') }} />
            <Choice title="Additional Services" sub="An ADD with its own number and title." active={phaseType === 'additional'}
              onClick={() => { setPhaseType('additional'); setStep(3); setError('') }} />
            <Choice title="Long Distance Travel" sub="A travel phase billed at a day rate." active={phaseType === 'travel'}
              onClick={() => { setPhaseType('travel'); setStep(3); setError('') }} />
          </>)}

          {step === 3 && (<>
            {inCore === 'link' && (
              <div>
                <label style={label}>BQE Core ID</label>
                <input style={mono} value={bqeCoreId} autoFocus placeholder="the phase's BQE Core record id"
                  onChange={e => setBqeCoreId(e.target.value)} />
              </div>
            )}

            {phaseType === 'basic' && (
              <div>
                <label style={label}>Phase</label>
                <select style={input} value={basicName} onChange={e => setBasicName(e.target.value)}>
                  <option value="">Choose a phase…</option>
                  {BASIC_PHASES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            )}

            {phaseType === 'additional' && (
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 110 }}>
                  <label style={label}>ADD number</label>
                  <input style={mono} type="number" min="1" value={asNumber} autoFocus onChange={e => setAsNumber(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={label}>Title</label>
                  <input style={input} value={asTitle} placeholder="e.g. Structural revisions" onChange={e => setAsTitle(e.target.value)} />
                </div>
              </div>
            )}

            {phaseType === 'travel' && (
              <div style={{ display: 'flex', gap: 12 }}>
                <div style={{ width: 110 }}>
                  <label style={label}>Travel number</label>
                  <input style={mono} type="number" min="1" value={ldtNumber} autoFocus onChange={e => setLdtNumber(e.target.value)} />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={label}>Travel date</label>
                  <input style={input} type="date" value={ldtDate} onChange={e => setLdtDate(e.target.value)} />
                </div>
              </div>
            )}

            {/* Contract type (Long Distance Travel is always Fixed Fee). */}
            <div style={{ display: 'flex', gap: 12 }}>
              {phaseType !== 'travel' && (
                <div style={{ flex: 1 }}>
                  <label style={label}>Contract type</label>
                  <select style={input} value={contractType} onChange={e => setContractType(e.target.value)}>
                    <option value="Fixed Fee">Fixed Fee</option>
                    <option value="Hourly">Hourly</option>
                  </select>
                </div>
              )}
              {(phaseType === 'travel' || contractType === 'Fixed Fee') && (
                <div style={{ flex: 1 }}>
                  <label style={label}>{phaseType === 'travel' ? 'Day-rate fee' : 'Contracted fee'}</label>
                  <input style={mono} type="number" min="0" value={fee} placeholder="0" onChange={e => setFee(e.target.value)} />
                </div>
              )}
            </div>

            {phaseType === 'basic' && (
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: 'var(--aj-text-mid)', cursor: 'pointer' }}>
                <input type="checkbox" checked={createMilestones} onChange={e => setCreateMilestones(e.target.checked)} />
                Populate this phase's milestones automatically
              </label>
            )}

            {inCore === 'create' && (
              <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 10, color: 'var(--aj-text-muted)' }}>
                Cortex will create this phase in BQE Core under the project and link it — no Core ID needed.
              </div>
            )}
          </>)}

          {error && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#C0392B' }}>{error}</div>}
        </div>

        {/* Footer */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, padding: '14px 20px', borderTop: '0.5px solid var(--aj-border)' }}>
          <button onClick={() => { if (step > 1) { setStep(step - 1); setError('') } else close() }} disabled={saving}
            style={{ ...btnBase, background: 'transparent', color: 'var(--aj-text-mid)', border: '0.5px solid var(--aj-border-mid)' }}>
            {step > 1 ? 'Back' : 'Cancel'}
          </button>
          {step === 3 && (
            <button onClick={submit} disabled={saving} style={{ ...btnBase, background: '#07272D', color: '#fff', opacity: saving ? 0.6 : 1 }}>
              {saving ? (inCore === 'create' ? 'Creating in Core…' : 'Adding…') : (inCore === 'create' ? 'Create in Core' : 'Add phase')}
            </button>
          )}
        </div>
      </div>
    </div>,
    document.body
  )
}
